import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { db } from "@/lib/firebase";
import { collection, query, where, getDocs, Timestamp } from "firebase/firestore";
import { formatCurrency } from "@/config/company.config";
import { generatePdfFromHtml } from "@/utils/pdf-generator";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend 
} from "recharts";
import { 
  TrendingUp, TrendingDown, DollarSign, Users, Briefcase, Clock, 
  Target, Award, Calendar, Minus, BarChart3, PieChart as PieChartIcon,
  Download, FileText
} from "lucide-react";
import { format, subMonths, startOfMonth, endOfMonth, isWithinInterval } from "date-fns";
import { fr } from "date-fns/locale";

const translations = {
  en: {
    title: "Analytics",
    subtitle: "Track your performance and business metrics",
    period: "Period",
    thisMonth: "This Month",
    lastMonth: "Last Month",
    last3Months: "Last 3 Months",
    last6Months: "Last 6 Months",
    thisYear: "This Year",
    revenue: "Revenue",
    totalRevenue: "Total Revenue",
    revenueGrowth: "Revenue Growth",
    clients: "Clients",
    activeClients: "Active Clients",
    newClients: "New Clients",
    clientRetention: "Client Retention",
    mandates: "Mandates",
    activeMandates: "Active Mandates",
    completedMandates: "Completed",
    avgMandateValue: "Avg Mandate Value",
    performance: "Performance",
    billableHours: "Billable Hours",
    utilizationRate: "Utilization Rate",
    avgResponseTime: "Avg Response Time",
    overview: "Overview",
    revenueChart: "Revenue Trend",
    clientsChart: "Client Activity",
    mandatesChart: "Mandates Distribution",
    hoursChart: "Hours Breakdown",
    vsLastPeriod: "vs last period",
    hours: "hours",
    days: "days",
    exportPdf: "Export PDF",
    exporting: "Exporting...",
    exportSuccess: "PDF exported successfully",
    exportError: "Failed to export PDF",
    noData: "No data available for this period",
  },
  fr: {
    title: "Analytique",
    subtitle: "Suivez vos performances et métriques d'affaires",
    period: "Période",
    thisMonth: "Ce Mois",
    lastMonth: "Mois Dernier",
    last3Months: "3 Derniers Mois",
    last6Months: "6 Derniers Mois",
    thisYear: "Cette Année",
    revenue: "Revenus",
    totalRevenue: "Revenus Totaux",
    revenueGrowth: "Croissance",
    clients: "Clients",
    activeClients: "Clients Actifs",
    newClients: "Nouveaux Clients",
    clientRetention: "Rétention Client",
    mandates: "Mandats",
    activeMandates: "Mandats Actifs",
    completedMandates: "Complétés",
    avgMandateValue: "Valeur Moyenne",
    performance: "Performance",
    billableHours: "Heures Facturables",
    utilizationRate: "Taux Utilisation",
    avgResponseTime: "Temps Réponse Moy.",
    overview: "Aperçu",
    revenueChart: "Tendance Revenus",
    clientsChart: "Activité Clients",
    mandatesChart: "Distribution Mandats",
    hoursChart: "Répartition Heures",
    vsLastPeriod: "vs période précédente",
    hours: "heures",
    days: "jours",
    exportPdf: "Exporter PDF",
    exporting: "Exportation...",
    exportSuccess: "PDF exporté avec succès",
    exportError: "Échec de l'exportation PDF",
    noData: "Aucune donnée disponible pour cette période",
  },
};

interface AnalyticsData {
  revenue: {
    total: number;
    growth: number;
    target: number;
    monthly: { month: string; revenue: number }[];
  };
  clients: {
    active: number;
    new: number;
    retention: number;
    growth: number;
    activity: { month: string; active: number; new: number }[];
  };
  mandates: {
    active: number;
    completed: number;
    pending: number;
    avgValue: number;
    distribution: { name: string; value: number; color: string }[];
  };
  performance: {
    billableHours: number;
    totalHours: number;
    utilizationRate: number;
    avgResponseTime: number;
    hoursBreakdown: { category: string; hours: number; color: string }[];
  };
}

const DEFAULT_ANALYTICS: AnalyticsData = {
  revenue: { total: 0, growth: 0, target: 100000, monthly: [] },
  clients: { active: 0, new: 0, retention: 0, growth: 0, activity: [] },
  mandates: { active: 0, completed: 0, pending: 0, avgValue: 0, distribution: [] },
  performance: { billableHours: 0, totalHours: 168, utilizationRate: 0, avgResponseTime: 0, hoursBreakdown: [] },
};

const MANDATE_TYPE_COLORS: Record<string, string> = {
  legal: "#3b82f6",
  tax: "#10b981",
  financial: "#f59e0b",
  immigration: "#8b5cf6",
  notary: "#ec4899",
  accounting: "#06b6d4",
  due_diligence: "#84cc16",
  other: "#6b7280",
};

interface FirestoreMandate {
  id: string;
  totalFee?: number;
  status?: string;
  type?: string;
  loggedHours?: number;
  createdAt?: Date;
  startDate?: Date;
}

interface FirestoreClient {
  id: string;
  status?: string;
  createdAt?: Date;
}

export default function PartnerAnalytics() {
  const { partnerType } = useParams<{ partnerType: string }>();
  const { user } = useAuth();
  const { language } = useLanguage();
  const { toast } = useToast();
  const t = translations[language as keyof typeof translations] || translations.en;
  const reportRef = useRef<HTMLDivElement>(null);

  const [period, setPeriod] = useState("last3Months");
  const [analytics, setAnalytics] = useState<AnalyticsData>(DEFAULT_ANALYTICS);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    if (user) {
      loadAnalytics();
    }
  }, [user, period]);

  const getPeriodRange = () => {
    const now = new Date();
    switch (period) {
      case "thisMonth":
        return { start: startOfMonth(now), end: now, months: 1 };
      case "lastMonth":
        return { start: startOfMonth(subMonths(now, 1)), end: endOfMonth(subMonths(now, 1)), months: 1 };
      case "last3Months":
        return { start: startOfMonth(subMonths(now, 2)), end: now, months: 3 };
      case "last6Months":
        return { start: startOfMonth(subMonths(now, 5)), end: now, months: 6 };
      case "thisYear":
        return { start: new Date(now.getFullYear(), 0, 1), end: now, months: 12 };
      default:
        return { start: startOfMonth(subMonths(now, 2)), end: now, months: 3 };
    }
  };

  const loadAnalytics = async () => {
    if (!user) return;
    setIsLoading(true);

    try {
      const { start, end, months } = getPeriodRange();
      
      // Fetch mandates
      const mandatesQuery = query(
        collection(db, "partnerMandates"),
        where("partnerId", "==", user.email)
      );
      const mandatesSnapshot = await getDocs(mandatesQuery);
      const mandates: FirestoreMandate[] = mandatesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        startDate: doc.data().startDate?.toDate(),
      } as FirestoreMandate));

      // Fetch clients
      const clientsQuery = query(
        collection(db, "partnerClients"),
        where("partnerId", "==", user.email)
      );
      const clientsSnapshot = await getDocs(clientsQuery);
      const clients: FirestoreClient[] = clientsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
      } as FirestoreClient));

      // Calculate revenue
      const periodMandates = mandates.filter(m => 
        m.createdAt && isWithinInterval(m.createdAt, { start, end })
      );
      const totalRevenue = periodMandates.reduce((sum, m) => sum + (m.totalFee || 0), 0);
      
      // Calculate monthly revenue
      const monthlyRevenue: { month: string; revenue: number }[] = [];
      for (let i = months - 1; i >= 0; i--) {
        const monthStart = startOfMonth(subMonths(new Date(), i));
        const monthEnd = endOfMonth(subMonths(new Date(), i));
        const monthMandates = mandates.filter(m => 
          m.createdAt && isWithinInterval(m.createdAt, { start: monthStart, end: monthEnd })
        );
        const monthRevenue = monthMandates.reduce((sum, m) => sum + (m.totalFee || 0), 0);
        monthlyRevenue.push({
          month: format(monthStart, 'MMM', { locale: language === 'fr' ? fr : undefined }),
          revenue: monthRevenue,
        });
      }

      // Calculate client metrics
      const periodClients = clients.filter(c => 
        c.createdAt && isWithinInterval(c.createdAt, { start, end })
      );
      const activeClients = clients.filter(c => c.status !== 'inactive').length;
      const newClients = periodClients.length;

      // Calculate client activity by month
      const clientActivity: { month: string; active: number; new: number }[] = [];
      for (let i = months - 1; i >= 0; i--) {
        const monthStart = startOfMonth(subMonths(new Date(), i));
        const monthEnd = endOfMonth(subMonths(new Date(), i));
        const monthNewClients = clients.filter(c => 
          c.createdAt && isWithinInterval(c.createdAt, { start: monthStart, end: monthEnd })
        ).length;
        clientActivity.push({
          month: format(monthStart, 'MMM', { locale: language === 'fr' ? fr : undefined }),
          active: activeClients,
          new: monthNewClients,
        });
      }

      // Calculate mandate metrics
      const activeMandates = mandates.filter(m => m.status === 'active').length;
      const completedMandates = mandates.filter(m => m.status === 'completed').length;
      const pendingMandates = mandates.filter(m => m.status === 'pending').length;
      const avgMandateValue = mandates.length > 0 
        ? mandates.reduce((sum, m) => sum + (m.totalFee || 0), 0) / mandates.length 
        : 0;

      // Calculate mandate distribution by type
      const typeCount: Record<string, number> = {};
      mandates.forEach(m => {
        const type = m.type || 'other';
        typeCount[type] = (typeCount[type] || 0) + 1;
      });
      const totalMandates = mandates.length || 1;
      const distribution = Object.entries(typeCount).map(([name, count]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1).replace('_', ' '),
        value: Math.round((count / totalMandates) * 100),
        color: MANDATE_TYPE_COLORS[name] || "#6b7280",
      }));

      // Calculate performance metrics
      const billableHours = mandates.reduce((sum, m) => sum + (m.loggedHours || 0), 0);
      const totalHours = 168 * months; // Assuming 168 working hours per month
      const utilizationRate = totalHours > 0 ? (billableHours / totalHours) * 100 : 0;

      // Hours breakdown by type
      const hoursBreakdown: { category: string; hours: number; color: string }[] = [];
      const typeHours: Record<string, number> = {};
      mandates.forEach(m => {
        const type = m.type || 'other';
        typeHours[type] = (typeHours[type] || 0) + (m.loggedHours || 0);
      });
      Object.entries(typeHours).forEach(([type, hours]) => {
        hoursBreakdown.push({
          category: type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' '),
          hours,
          color: MANDATE_TYPE_COLORS[type] || "#6b7280",
        });
      });

      // Calculate growth (compare with previous period)
      const previousStart = subMonths(start, months);
      const previousMandates = mandates.filter(m => 
        m.createdAt && isWithinInterval(m.createdAt, { start: previousStart, end: start })
      );
      const previousRevenue = previousMandates.reduce((sum, m) => sum + (m.totalFee || 0), 0);
      const revenueGrowth = previousRevenue > 0 
        ? ((totalRevenue - previousRevenue) / previousRevenue) * 100 
        : totalRevenue > 0 ? 100 : 0;

      const previousClients = clients.filter(c => 
        c.createdAt && isWithinInterval(c.createdAt, { start: previousStart, end: start })
      );
      const clientGrowth = previousClients.length > 0 
        ? ((newClients - previousClients.length) / previousClients.length) * 100 
        : newClients > 0 ? 100 : 0;

      setAnalytics({
        revenue: {
          total: totalRevenue,
          growth: Math.round(revenueGrowth * 10) / 10,
          target: 100000,
          monthly: monthlyRevenue,
        },
        clients: {
          active: activeClients,
          new: newClients,
          retention: activeClients > 0 ? Math.round((activeClients / (clients.length || 1)) * 100) : 0,
          growth: Math.round(clientGrowth * 10) / 10,
          activity: clientActivity,
        },
        mandates: {
          active: activeMandates,
          completed: completedMandates,
          pending: pendingMandates,
          avgValue: Math.round(avgMandateValue),
          distribution: distribution.length > 0 ? distribution : [{ name: 'No Data', value: 100, color: '#6b7280' }],
        },
        performance: {
          billableHours,
          totalHours,
          utilizationRate: Math.round(utilizationRate * 10) / 10,
          avgResponseTime: 2.4, // This would need a separate tracking system
          hoursBreakdown: hoursBreakdown.length > 0 ? hoursBreakdown : [{ category: 'No Data', hours: 0, color: '#6b7280' }],
        },
      });
    } catch (error) {
      console.error("Error loading analytics:", error);
      setAnalytics(DEFAULT_ANALYTICS);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportPdf = async () => {
    setIsExporting(true);
    try {
      const periodLabel = t[period as keyof typeof t] || period;
      const htmlContent = generateReportHtml(periodLabel);
      
      await generatePdfFromHtml(htmlContent, {
        filename: `analytics-report-${partnerType}-${format(new Date(), 'yyyy-MM-dd')}.pdf`,
        orientation: 'portrait',
        pageSize: 'a4',
      });

      toast({
        title: t.exportSuccess,
        description: `Report exported successfully`,
      });
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast({
        title: t.exportError,
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const generateReportHtml = (periodLabel: string) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; color: #333; }
          h1 { color: #1a365d; border-bottom: 2px solid #3b82f6; padding-bottom: 10px; }
          h2 { color: #2563eb; margin-top: 30px; }
          .header { text-align: center; margin-bottom: 40px; }
          .period { color: #666; font-size: 14px; }
          .kpi-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin: 20px 0; }
          .kpi-card { border: 1px solid #e5e7eb; padding: 20px; border-radius: 8px; }
          .kpi-title { font-size: 12px; color: #666; text-transform: uppercase; }
          .kpi-value { font-size: 24px; font-weight: bold; color: #1a365d; margin-top: 5px; }
          .kpi-change { font-size: 12px; margin-top: 5px; }
          .positive { color: #10b981; }
          .negative { color: #ef4444; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { padding: 12px; text-align: left; border-bottom: 1px solid #e5e7eb; }
          th { background: #f9fafb; font-weight: 600; }
          .footer { margin-top: 40px; text-align: center; color: #666; font-size: 12px; border-top: 1px solid #e5e7eb; padding-top: 20px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${t.title} Report</h1>
          <p class="period">${periodLabel} - ${format(new Date(), 'MMMM d, yyyy', { locale: language === 'fr' ? fr : undefined })}</p>
        </div>

        <h2>${t.overview}</h2>
        <div class="kpi-grid">
          <div class="kpi-card">
            <div class="kpi-title">${t.totalRevenue}</div>
            <div class="kpi-value">${formatCurrency(analytics.revenue.total)}</div>
            <div class="kpi-change ${analytics.revenue.growth >= 0 ? 'positive' : 'negative'}">
              ${analytics.revenue.growth >= 0 ? '+' : ''}${analytics.revenue.growth}% ${t.vsLastPeriod}
            </div>
          </div>
          <div class="kpi-card">
            <div class="kpi-title">${t.activeClients}</div>
            <div class="kpi-value">${analytics.clients.active}</div>
            <div class="kpi-change ${analytics.clients.growth >= 0 ? 'positive' : 'negative'}">
              ${analytics.clients.growth >= 0 ? '+' : ''}${analytics.clients.growth}% ${t.vsLastPeriod}
            </div>
          </div>
          <div class="kpi-card">
            <div class="kpi-title">${t.activeMandates}</div>
            <div class="kpi-value">${analytics.mandates.active}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-title">${t.utilizationRate}</div>
            <div class="kpi-value">${analytics.performance.utilizationRate}%</div>
          </div>
        </div>

        <h2>${t.mandates}</h2>
        <div class="kpi-grid">
          <div class="kpi-card">
            <div class="kpi-title">${t.activeMandates}</div>
            <div class="kpi-value">${analytics.mandates.active}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-title">${t.completedMandates}</div>
            <div class="kpi-value">${analytics.mandates.completed}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-title">${t.avgMandateValue}</div>
            <div class="kpi-value">${formatCurrency(analytics.mandates.avgValue)}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-title">${t.billableHours}</div>
            <div class="kpi-value">${analytics.performance.billableHours} ${t.hours}</div>
          </div>
        </div>

        <h2>${t.revenueChart}</h2>
        <table>
          <thead>
            <tr>
              <th>Month</th>
              <th>Revenue</th>
            </tr>
          </thead>
          <tbody>
            ${analytics.revenue.monthly.map(m => `
              <tr>
                <td>${m.month}</td>
                <td>${formatCurrency(m.revenue)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <h2>${t.mandatesChart}</h2>
        <table>
          <thead>
            <tr>
              <th>Type</th>
              <th>Percentage</th>
            </tr>
          </thead>
          <tbody>
            ${analytics.mandates.distribution.map(d => `
              <tr>
                <td>${d.name}</td>
                <td>${d.value}%</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="footer">
          <p>Generated on ${format(new Date(), 'MMMM d, yyyy \'at\' h:mm a', { locale: language === 'fr' ? fr : undefined })}</p>
          <p>AcquizGroup Partner Analytics</p>
        </div>
      </body>
      </html>
    `;
  };

  const StatCard = ({ 
    title, 
    value, 
    change, 
    icon: Icon, 
    suffix,
    prefix 
  }: { 
    title: string; 
    value: string | number; 
    change?: number; 
    icon: any;
    suffix?: string;
    prefix?: string;
  }) => (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">
          {prefix}{value}{suffix}
        </div>
        {change !== undefined && (
          <div className={`flex items-center gap-1 text-xs mt-1 ${
            change > 0 ? 'text-green-600' : change < 0 ? 'text-red-600' : 'text-muted-foreground'
          }`}>
            {change > 0 ? <TrendingUp className="h-3 w-3" /> : 
             change < 0 ? <TrendingDown className="h-3 w-3" /> : 
             <Minus className="h-3 w-3" />}
            {Math.abs(change)}% {t.vsLastPeriod}
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" ref={reportRef}>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t.title}</h1>
          <p className="text-muted-foreground mt-2">{t.subtitle}</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[180px]">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue placeholder={t.period} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="thisMonth">{t.thisMonth}</SelectItem>
              <SelectItem value="lastMonth">{t.lastMonth}</SelectItem>
              <SelectItem value="last3Months">{t.last3Months}</SelectItem>
              <SelectItem value="last6Months">{t.last6Months}</SelectItem>
              <SelectItem value="thisYear">{t.thisYear}</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={handleExportPdf} disabled={isExporting} variant="outline">
            {isExporting ? (
              <>
                <Clock className="h-4 w-4 mr-2 animate-spin" />
                {t.exporting}
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                {t.exportPdf}
              </>
            )}
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title={t.totalRevenue}
          value={formatCurrency(analytics.revenue.total)}
          change={analytics.revenue.growth}
          icon={DollarSign}
        />
        <StatCard
          title={t.activeClients}
          value={analytics.clients.active}
          change={analytics.clients.growth}
          icon={Users}
        />
        <StatCard
          title={t.activeMandates}
          value={analytics.mandates.active}
          icon={Briefcase}
        />
        <StatCard
          title={t.utilizationRate}
          value={analytics.performance.utilizationRate}
          suffix="%"
          icon={Target}
        />
      </div>

      {/* Charts Tabs */}
      <Tabs defaultValue="revenue" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="revenue" className="flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            <span className="hidden sm:inline">{t.revenue}</span>
          </TabsTrigger>
          <TabsTrigger value="clients" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            <span className="hidden sm:inline">{t.clients}</span>
          </TabsTrigger>
          <TabsTrigger value="mandates" className="flex items-center gap-2">
            <PieChartIcon className="h-4 w-4" />
            <span className="hidden sm:inline">{t.mandates}</span>
          </TabsTrigger>
          <TabsTrigger value="performance" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            <span className="hidden sm:inline">{t.performance}</span>
          </TabsTrigger>
        </TabsList>

        {/* Revenue Tab */}
        <TabsContent value="revenue" className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-3">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>{t.revenueChart}</CardTitle>
                <CardDescription>
                  {language === 'fr' ? 'Évolution mensuelle des revenus' : 'Monthly revenue evolution'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={analytics.revenue.monthly}>
                    <defs>
                      <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="month" className="text-xs" />
                    <YAxis tickFormatter={(v) => `$${v/1000}k`} className="text-xs" />
                    <Tooltip 
                      formatter={(value: number) => formatCurrency(value)}
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="revenue" 
                      stroke="hsl(var(--primary))" 
                      fill="url(#revenueGradient)"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t.revenueGrowth}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center">
                  <div className="text-4xl font-bold text-primary">
                    {analytics.revenue.growth > 0 ? '+' : ''}{analytics.revenue.growth}%
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{t.vsLastPeriod}</p>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{t.totalRevenue}</span>
                    <span className="font-medium">{formatCurrency(analytics.revenue.total)}</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className="bg-primary h-2 rounded-full transition-all"
                      style={{ width: `${Math.min((analytics.revenue.total / analytics.revenue.target) * 100, 100)}%` }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground text-center">
                    {formatCurrency(analytics.revenue.total)} / {formatCurrency(analytics.revenue.target)} target
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Clients Tab */}
        <TabsContent value="clients" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <StatCard title={t.activeClients} value={analytics.clients.active} icon={Users} change={analytics.clients.growth} />
            <StatCard title={t.newClients} value={analytics.clients.new} icon={Users} />
            <StatCard title={t.clientRetention} value={analytics.clients.retention} suffix="%" icon={Award} />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>{t.clientsChart}</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analytics.clients.activity}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="month" className="text-xs" />
                  <YAxis className="text-xs" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Bar dataKey="active" name={t.activeClients} fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="new" name={t.newClients} fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Mandates Tab */}
        <TabsContent value="mandates" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <StatCard title={t.activeMandates} value={analytics.mandates.active} icon={Briefcase} />
            <StatCard title={t.completedMandates} value={analytics.mandates.completed} icon={Award} />
            <StatCard title={t.avgMandateValue} value={formatCurrency(analytics.mandates.avgValue)} icon={DollarSign} />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>{t.mandatesChart}</CardTitle>
            </CardHeader>
            <CardContent className="flex justify-center">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={analytics.mandates.distribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {analytics.mandates.distribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value: number) => `${value}%`}
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Performance Tab */}
        <TabsContent value="performance" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <StatCard 
              title={t.billableHours} 
              value={analytics.performance.billableHours} 
              suffix={` ${t.hours}`}
              icon={Clock} 
            />
            <StatCard 
              title={t.utilizationRate} 
              value={analytics.performance.utilizationRate} 
              suffix="%" 
              icon={Target} 
            />
            <StatCard 
              title={t.avgResponseTime} 
              value={analytics.performance.avgResponseTime} 
              suffix={` ${t.days}`}
              icon={Clock} 
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>{t.hoursChart}</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analytics.performance.hoursBreakdown} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis type="number" className="text-xs" />
                  <YAxis dataKey="category" type="category" width={120} className="text-xs" />
                  <Tooltip 
                    formatter={(value: number) => `${value} ${t.hours}`}
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="hours" radius={[0, 4, 4, 0]}>
                    {analytics.performance.hoursBreakdown.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
